/**
 * Customer Group Manager
 * Unified script to handle customer group selection across cart drawer and main cart
 */

class CustomerGroupManager {
  constructor() {
    this.storageKey = 'cart_category';
    this.attributeKey = 'kundengruppe';
    this.defaultValue = 'ONLINESHOP';
    this.initialized = new Set();
    
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.initializeCartAttributes();
    this.setupSelectElements();
    this.setupEventListeners();
  }

  async initializeCartAttributes() {
    try {
      // Check if cart has items and no kundengruppe attribute
      const cartData = await this.fetchCartData();
      
      if (cartData.item_count > 0 && (!cartData.attributes || !cartData.attributes[this.attributeKey])) {
        // Cart has items but no kundengruppe attribute, initialize with default
        await this.updateCartAttribute(this.defaultValue);
        console.debug(`Cart initialized with default kundengruppe: ${this.defaultValue}`);
      }
    } catch (error) {
      console.error('Error initializing cart attributes:', error);
    }
  }

  setupSelectElements() {
    // Find all customer group select elements
    const selects = document.querySelectorAll('#cart-category-select, #cart-drawer-category-select');
    
    selects.forEach(select => {
      if (!this.initialized.has(select.id)) {
        this.initializeSelect(select);
        this.initialized.add(select.id);
      }
    });
  }

  async initializeSelect(select) {
    if (!select) return;

    try {
      // Get current cart data
      const cartData = await this.fetchCartData();
      let selectedValue = null;
      let shouldSetValue = false;
      let shouldInitializeDefault = false;

      // Priority order: cart attributes > localStorage > initialize with default
      if (cartData.attributes && cartData.attributes[this.attributeKey]) {
        selectedValue = cartData.attributes[this.attributeKey];
        shouldSetValue = true;
      } else {
        const storedValue = localStorage.getItem(this.storageKey);
        if (storedValue && storedValue !== this.defaultValue) {
          selectedValue = storedValue;
          shouldSetValue = true;
        } else {
          // No existing value, initialize with default
          selectedValue = this.defaultValue;
          shouldInitializeDefault = true;
        }
      }

      if (shouldSetValue && selectedValue) {
        // Validate that the value exists in options
        const optionExists = Array.from(select.options).some(option => option.value === selectedValue);
        
        if (optionExists) {
          select.value = selectedValue;
          // Update localStorage and cart attributes
          localStorage.setItem(this.storageKey, selectedValue);
          await this.updateCartAttribute(selectedValue);
          // Sync with other selects
          this.syncAllSelects(selectedValue, select);
          console.debug(`Initialized ${select.id} with value: ${selectedValue}`);
        } else {
          // Invalid value, clear it and initialize with default
          localStorage.removeItem(this.storageKey);
          select.value = '';
          await this.updateCartAttribute(this.defaultValue);
          console.debug(`Cleared invalid value for ${select.id}, initialized with default: ${this.defaultValue}`);
        }
      } else if (shouldInitializeDefault) {
        // Initialize cart with default value but leave select showing placeholder
        select.value = '';
        await this.updateCartAttribute(this.defaultValue);
        console.debug(`Initialized cart with default value: ${this.defaultValue}, showing placeholder`);
      } else {
        // No existing selection, leave placeholder visible
        select.value = '';
        console.debug(`No existing selection for ${select.id}, showing placeholder`);
      }

    } catch (error) {
      console.error('Error initializing select:', error);
      // On error, initialize with default and leave placeholder visible
      select.value = '';
      try {
        await this.updateCartAttribute(this.defaultValue);
        console.debug(`Error occurred, initialized cart with default: ${this.defaultValue}`);
      } catch (initError) {
        console.error('Error initializing cart with default value:', initError);
      }
    }
  }

  async fetchCartData() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }
    return response.json();
  }

  async updateCartAttribute(value) {
    try {
      const formData = new FormData();
      formData.append(`attributes[${this.attributeKey}]`, value || this.defaultValue);
      
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Cart update failed');
      }

      const data = await response.json();
      console.debug('Cart attributes updated:', data);
      return data;

    } catch (error) {
      console.error('Cart update error:', error);
      throw error;
    }
  }

  syncAllSelects(value, excludeSelect = null) {
    const selects = document.querySelectorAll('#cart-category-select, #cart-drawer-category-select');
    
    selects.forEach(select => {
      if (select !== excludeSelect && select.value !== value) {
        select.value = value;
      }
    });
  }

  async handleSelectChange(select) {
    const selectedValue = select.value;

    try {
      if (selectedValue && selectedValue !== '') {
        // User selected a real value
        localStorage.setItem(this.storageKey, selectedValue);
        await this.updateCartAttribute(selectedValue);
        this.syncAllSelects(selectedValue, select);
        console.debug(`Customer group changed to: ${selectedValue}`);
      } else {
        // User selected placeholder (empty value) - this shouldn't normally happen due to required attribute
        // But handle it gracefully
        localStorage.removeItem(this.storageKey);
        await this.updateCartAttribute(this.defaultValue);
        console.debug(`Customer group cleared, using default: ${this.defaultValue}`);
      }

      // Hide error messages when a valid selection is made
      const errorEl = document.getElementById(select.id + '-error');
      if (errorEl) {
        errorEl.hidden = !!selectedValue;
      }

    } catch (error) {
      console.error('Error handling select change:', error);
    }
  }

  setupEventListeners() {
    // Handle select changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('#cart-category-select, #cart-drawer-category-select')) {
        this.handleSelectChange(event.target);
      }
    });

    // Handle cart drawer opening and cart updates
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => {
        this.initializeCartAttributes();
        this.setupSelectElements();
      }, 200);
    });

    // Handle cart drawer mutations
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (cartDrawer.classList.contains('active')) {
              setTimeout(() => {
                this.initializeCartAttributes();
                this.setupSelectElements();
              }, 300);
            }
          }
        });
      });
      observer.observe(cartDrawer, { attributes: true });
    }

    // Listen for cart updates (when items are added/removed)
    document.addEventListener('cart:updated', () => {
      setTimeout(() => {
        this.initializeCartAttributes();
        this.setupSelectElements();
      }, 100);
    });

    // Clean localStorage on checkout
    this.setupCheckoutCleanup();
  }

  setupCheckoutCleanup() {
    // Clean localStorage when going to checkout
    document.addEventListener('click', (event) => {
      const target = event.target.closest('[name="add"], [href*="checkout"], [onclick*="checkout"], .btn[href*="checkout"]');
      if (target) {
        // Small delay to ensure cart is updated before checkout
        setTimeout(() => {
          localStorage.removeItem(this.storageKey);
          console.debug('Customer group localStorage cleaned for checkout');
        }, 100);
      }
    });

    // Also clean on form submission to checkout
    document.addEventListener('submit', (event) => {
      if (event.target.action && event.target.action.includes('checkout')) {
        localStorage.removeItem(this.storageKey);
        console.debug('Customer group localStorage cleaned for checkout form');
      }
    });
  }

  // Public method to reinitialize (useful for dynamic content)
  async reinitialize() {
    this.initialized.clear();
    await this.initializeCartAttributes();
    this.setupSelectElements();
  }

  // Public method to get current value
  getCurrentValue() {
    return localStorage.getItem(this.storageKey) || this.defaultValue;
  }

  // Public method to set value programmatically
  async setValue(value) {
    if (value && value !== '') {
      localStorage.setItem(this.storageKey, value);
      await this.updateCartAttribute(value);
      this.syncAllSelects(value);
    } else {
      localStorage.removeItem(this.storageKey);
      await this.updateCartAttribute(this.defaultValue);
      this.syncAllSelects('');
    }
  }
}

// Create global instance
window.CustomerGroupManager = new CustomerGroupManager();

// Export for modules if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomerGroupManager;
}
